import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import DashboardClinicalRosterItem from "components/DashboardClinicalRosterItem/DashboardClinicalRosterItem";
import DashboardSelect from "components/DashboardSelect";
import DashboardSearch from "components/DashboardSearch";
import DashboardSortTableHeader from "components/DashboardSortTableHeader";
import DashboardTablePagination from "components/DashboardTablePagination";
import { DashboardItemsPerPage } from "components/DashboardItemsPerPage";
import { DashboardContactInformationModal } from "components/DashboardContactInformationModal";
import { Button } from "@cpnw/ui";
import styles from "./DashboardClinicalRosterTable.module.css";

const DashboardClinicalRosterTable = ({ items }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentItems, setCurrentItems] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedSchool, setSelectedSchool] = useState("");
  const [selectedStartDate, setSelectedStartDate] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [totalItems, setTotalItems] = useState(items.length);
  const [modalContactInfo, setModalContactInfo] = useState(null);
  const [sortCriteria, setSortCriteria] = useState({
    column: "startDate",
    direction: "asc",
  });

  const sortedItems = useMemo(() => {
    // Filter items based on search term
    let filteredItems = items.filter((item) =>
      Object.values(item).some((value) => 
        !!value ? value.toString().toLowerCase().includes(searchTerm.toLowerCase()) : false
      )
    );

    // Apply additional filters based on selected criteria
    if (selectedLocation) {
      filteredItems = filteredItems.filter(
        (item) => item.location === selectedLocation
      );
    }
    if (selectedSchool) {
      filteredItems = filteredItems.filter(
        (item) => item.school === selectedSchool
      );
    }
    if (selectedStartDate) {
      filteredItems = filteredItems.filter(
        (item) => formatDate(item.startDate) === selectedStartDate
      );
    }

    // Sort the filtered items
    const sorted = filteredItems.sort((a, b) => {
      const { column, direction } = sortCriteria;
      const aValue = a[column];
      const bValue = b[column];
      return direction === "asc"
        ? aValue > bValue
          ? 1
          : -1
        : aValue < bValue
          ? 1
          : -1;
    });

    return sorted;
  }, [
    items,
    searchTerm,
    selectedLocation,
    selectedSchool,
    selectedStartDate,
    sortCriteria,
  ]);

  // Update totalItems whenever sortedItems changes
  useEffect(() => {
    setTotalItems(sortedItems.length);
  }, [sortedItems]);

  useEffect(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const newCurrentItems = sortedItems.slice(
      indexOfFirstItem,
      indexOfLastItem
    );

    setCurrentItems(newCurrentItems);
  }, [currentPage, itemsPerPage, sortedItems]);

  // Button Functions
  const handleShowAll = () => {
    setCurrentItems(sortedItems.slice(0, itemsPerPage)); // Display first page of sorted items
    setCurrentPage(1); // Reset to first page when showing all items
    setTotalItems(sortedItems.length);
  };

  const handleShowSelected = () => {
    if (selectedItems && selectedItems.length > 0) {
      const selectedItemsData = selectedItems.map((id) =>
        items.find((item) => item.id === id)
      );

      // Paginate the selected items based on the current page and items per page
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = Math.min(
        startIndex + itemsPerPage,
        selectedItemsData.length
      );
      const paginatedItems = selectedItemsData.slice(startIndex, endIndex);

      setCurrentItems(paginatedItems);
      setTotalItems(selectedItemsData.length);
    } 
  };

  // Select Filtering
  const locationsOptions = Array.from(
    new Set(items.map((item) => item.location))
  );
  const schoolsOptions = Array.from(new Set(items.map((item) => item.school)));
  const startDatesOptions = Array.from(
    new Set(
      items
        .map((item) => {
          return formatDate(item.startDate);
        })
        .filter((date) => date !== null)// Remove null values from the array
        .sort((a, b) => new Date(b) - new Date(a))
    )
  );

  function formatDate(dateString) {
    // Check if item.startDate is not null or undefined
    if (dateString) {
      const utcDateString = dateString;

      // Convert the UTC date to a Date object
      const date = new Date(utcDateString);

      // Extract components
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      const year = date.getUTCFullYear();

      // Format to "MM/dd/yyyy"
      const formattedDate = `${month}/${day}/${year}`;
      return formattedDate;
    }
  }

  const handleFilter = (value, selectType) => {
    switch (selectType) {
      case "Locations":
        setSelectedLocation(value);
        break;
      case "Schools":
        setSelectedSchool(value);
        break;
      case "StartDates":
        setSelectedStartDate(value);
        break;
      default:
        break;
    }

    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Handle pagination change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (perPage) => {
    setItemsPerPage(perPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Handle search input
  const handleSearch = (input) => {
    setSearchTerm(input);
    setCurrentPage(1); // Reset to first page when performing search    
  };

  // Handle sorting
  const handleSort = (column) => {
    setSortCriteria((prevCriteria) => {
      if (prevCriteria.column === column) {
        // If clicking the same column, toggle the direction
        const newDirection = prevCriteria.direction === "asc" ? "desc" : "asc";
        return { column, direction: newDirection };
      } else {
        // If clicking a different column, default to ascending order
        return { column, direction: "asc" };
      }
    });
  };

  // Handle Selection

  const handleSelect = (id) => {
    setSelectedItems((selectedItems) => {
      if (selectedItems.includes(id)) {
        return selectedItems.filter((selectedId) => selectedId !== id);
      } else {
        return [...selectedItems, id];
      }
    });
  };

  //  define onSelectSchools and onSelectStartDates functions
  const onSelectLocation = (value) => {
    setSelectedLocation(value); // Update the selected state
    handleFilter(); // Call the handleFilter function with the updated filter values
  };
  const onSelectSchool = (value) => {
    setSelectedSchool(value); // Update the selected  state
    handleFilter(); // Call the handleFilter function with the updated filter values
  };
  const onSelectStartDate = (value) => {
    setSelectedStartDate(value); // Update the selected location state
    handleFilter(); // Call the handleFilter function with the updated filter values
  };

  return (
    <div className={styles.clinicalRosterTableContainer}>
      {modalContactInfo && (
        <DashboardContactInformationModal
          onClose={() => {
            setModalContactInfo(null);
          }}
          contact={modalContactInfo}
          isOpen={true}
        />
      )}
      <div>
        <div className={styles.selectItems}>
          <div className={styles.selectItem}>
            <DashboardSelect
              placeholder="Locations - Select All"
              options={locationsOptions}
              tip="Click here to filter results by location"
              onSelect={(value) => onSelectLocation(value)}
            />
          </div>
          <div className={styles.selectItem}>
            <DashboardSelect
              placeholder="Schools - Select All"
              options={schoolsOptions}
              tip="Click here to filter results by school"
              onSelect={(value) => onSelectSchool(value)}
            />
          </div>
          <div className={styles.selectItem}>
            <DashboardSelect
              placeholder="Clinical Start Date - Select All"
              options={startDatesOptions}
              tip="Click here to filter results by clinical start date"
              onSelect={(value) => onSelectStartDate(value)}
            />
          </div>
        </div>
        <div>
          <div className={styles.itemsPerPage}>
            <DashboardItemsPerPage
              disabled={false}
              limit={itemsPerPage}
              limitOptions={[5, 10, 20, 50]}
              onLimitChange={handleItemsPerPageChange}
            />
          </div>
          <div className={styles.searchBox}>
            <DashboardSearch
              onInput={handleSearch}
              searchDelay={300}
              minChar={3}
            />
          </div>
          <div className={styles.filteringButtonContainer}>
            <Button
              text="Show All"
              className={styles.showAllButton}
              onClick={handleShowAll}
            />

            <Button
              text="Show Selected"
              className={styles.showSelectedButton}
              onClick={handleShowSelected}
              style={{ opacity: selectedItems && selectedItems.length > 0 ? 1 : 0.5 }} // Conditionally set opacity based on itemsSelected state
            />
          </div>
        </div>
      </div>
      <div className={styles.clinicalRoster}>
        <div className={styles.tableContainer}>
          <table className={styles.rosterTable}>
            <thead>
              <tr>
                <th></th>

                <th>
                  <DashboardSortTableHeader
                    title={"Name"}
                    onChange={(direction) => handleSort("name", direction)}
                    value={
                      sortCriteria.column === "name" ? sortCriteria.direction : ""
                    }
                  />
                </th>

                <th>
                  <DashboardSortTableHeader
                    title={"Location"}
                    onChange={(direction) => handleSort("location", direction)}
                    value={
                      sortCriteria.column === "location"
                        ? sortCriteria.direction
                        : ""
                    }
                  />
                </th>
                <th>
                  <DashboardSortTableHeader
                    title="Start Date"
                    onChange={(direction) => handleSort("startDate", direction)}
                    value={
                      sortCriteria.column === "startDate"
                        ? sortCriteria.direction
                        : ""
                    }
                  />
                </th>
                <th>
                  <DashboardSortTableHeader
                    title="End Date"
                    onChange={(direction) => handleSort("endDate", direction)}
                    value={
                      sortCriteria.column === "endDate"
                        ? sortCriteria.direction
                        : ""
                    }
                  />
                </th>
                <th>
                  <DashboardSortTableHeader
                    title="School"
                    onChange={(direction) => handleSort("school", direction)}
                    value={
                      sortCriteria.column === "school"
                        ? sortCriteria.direction
                        : ""
                    }
                  />
                </th>
                <th>
                  <DashboardSortTableHeader
                    title="Program"
                    onChange={(direction) => handleSort("program", direction)}
                    value={
                      sortCriteria.column === "program"
                        ? sortCriteria.direction
                        : ""
                    }
                  />
                </th>
                <th>
                  <DashboardSortTableHeader
                    title="Type"
                    onChange={(direction) => handleSort("type", direction)}
                    value={
                      sortCriteria.column === "type" ? sortCriteria.direction : ""
                    }
                  />
                </th>
              </tr>
            </thead>

            <tbody>
              {currentItems.map((item) => (
                <DashboardClinicalRosterItem
                  key={`contact_${item.id}_assignment_${item.assignmentId}`}
                  item={item}
                  selected={selectedItems.includes(item.id)}
                  onSelect={() => handleSelect(item.id)}
                  onClickName={(contact) => setModalContactInfo(contact)}
                />
              ))}
            </tbody>
          </table>
        </div>
        <div className={styles.space} />
        <DashboardTablePagination
          onPageChange={handlePageChange}
          total={totalItems}
          perPage={itemsPerPage}
        />
      </div>
    </div>
  );
};

DashboardClinicalRosterTable.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      location: PropTypes.string.isRequired,
      startDate: PropTypes.string.isRequired,
      endDate: PropTypes.string.isRequired,
      school: PropTypes.string.isRequired,
      program: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default DashboardClinicalRosterTable;
